import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { tenantPath } from "@/lib/tenant";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Loader2, Trash2, XCircle } from "lucide-react";
import { cancelJob, deleteJob, retryJob, readJobResult } from "./services";
import { jobEvent } from "./transport";
import { jobLabels, terminalStatuses as terminal } from "./access";
import { jobPage } from "./navigation";
import "./jobs.css";

const cancellable = ["UPLOADING", "QUEUED", "RUNNING", "FAILED", "NEEDS_REVIEW"];

export default function JobList({ rows }) {
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [deleting, setDeleting] = useState(null);
  const pending = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; pending.current?.abort(); };
  }, []);
  useEffect(() => {
    if (selected && !rows.some(row => row.id === selected.id)) setSelected(null);
    if (deleting && !rows.some(row => row.id === deleting.id)) setDeleting(null);
  }, [rows, selected, deleting]);
  const run = async (job, action) => {
    if (pending.current) return;
    const controller = new AbortController();
    pending.current = controller;
    setBusy(`${job.id}:${action}`); setError("");
    try {
      if (action === "result") {
        const value = await readJobResult(job.id, controller.signal);
        if (mounted.current && !controller.signal.aborted) setSelected({ id: job.id, value });
      } else {
        const operation = action === "cancel" ? cancelJob : action === "delete" ? deleteJob : retryJob;
        await operation(job.id);
        if (mounted.current && !controller.signal.aborted) { setSelected(null); setDeleting(null); }
        jobEvent();
      }
    } catch (failure) {
      if (mounted.current && !controller.signal.aborted) setError(apiError(failure));
    } finally {
      pending.current = null;
      if (mounted.current) setBusy("");
    }
  };
  return <div className="space-y-3">
    {error && !deleting && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {rows.map(job => <article className="border rounded-lg bg-card p-4 space-y-2" key={job.id} data-testid="background-job">
      <div className="flex flex-wrap items-start justify-between gap-2"><strong className="text-sm min-w-0 break-words">{job.label}</strong><span role="status" className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-medium">{job.cancel_requested && job.status === "RUNNING" ? <><Loader2 aria-hidden="true" className="h-3 w-3 animate-spin" />Cancelling…</> : jobLabels[job.status] || job.status}</span></div>
      <progress aria-label={`${job.label} progress`} className="job-progress" value={job.percentage || 0} max="100" />
      <p className="text-xs">{job.percentage || 0}% · {job.phase}{job.total > 0 ? ` · ${job.processed}/${job.total} committed units` : ""}</p>
      <p className="text-xs text-muted-foreground">Submitted {new Date(job.created_at).toLocaleString("en-GB", { timeZone: "Asia/Karachi" })} · Attempt {job.attempts || 0}</p>
      {job.error && <p role="alert" className="text-xs text-destructive">{job.error}</p>}
      {job.cancel_requested && job.status === "RUNNING" && <p className="text-xs text-muted-foreground">Stopping at a safe point. The current step may finish; completed records are preserved.</p>}
      <div className="flex flex-wrap gap-2 pt-1">
        <a className="inline-flex items-center rounded-md border px-3 py-2 text-sm font-medium text-primary hover:bg-accent" href={jobPage(job)}>Go to page</a>
        {cancellable.includes(job.status) && <Button type="button" size="sm" variant="outline" disabled={!!busy || !!job.cancel_requested} onClick={() => run(job, "cancel")}>
          {busy === `${job.id}:cancel` ? <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" /> : <XCircle aria-hidden="true" className="mr-2 h-4 w-4" />}Cancel job</Button>}
        {terminal.includes(job.status) && (job.has_result || (job.has_result === undefined && job.status !== "CANCELLED")) && <Button type="button" size="sm" variant="outline" disabled={!!busy} onClick={() => run(job, "result")}>View result</Button>}
        {["FAILED", "NEEDS_REVIEW"].includes(job.status) && <Button type="button" size="sm" variant="outline" disabled={!!busy} onClick={() => run(job, "retry")}>Retry same job</Button>}
        <Button type="button" size="sm" variant="outline" className="text-destructive hover:text-destructive" disabled={!!busy || !terminal.includes(job.status)} title={!terminal.includes(job.status) ? "Cancel and wait until the job stops before deleting" : "Delete from activity"} onClick={() => { setError(""); setDeleting(job); }}><Trash2 aria-hidden="true" className="mr-2 h-4 w-4" />Delete</Button>
      </div>
      {selected?.id === job.id && <JobResult value={selected.value} />}
    </article>)}
    <AlertDialog open={!!deleting} onOpenChange={open => { if (!open && !pending.current) setDeleting(null); }}>
      <AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>Delete job from activity?</AlertDialogTitle>
          <AlertDialogDescription>{deleting?.label} will be removed from the Jobs panel and All jobs. Completed bookings, payments, ledger entries and the job's audit history are preserved.</AlertDialogDescription></AlertDialogHeader>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <AlertDialogFooter><AlertDialogCancel disabled={!!busy}>Keep job</AlertDialogCancel>
          <Button type="button" variant="destructive" disabled={!!busy} onClick={() => deleting && run(deleting, "delete")}>{busy ? <><Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />Deleting…</> : "Delete job"}</Button></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>;
}

function JobResult({ value }) {
  const { user } = useAuth();
  const bookingList = user?.role === "sales_dealer" ? "/dealer/bookings" : "/admin/bookings";
  const bookings = value?.bookings || (value?.booking ? [value.booking] : value?.booking_id && value?.id ? [value] : []);
  const rows = value?.results || value?.rows || value?.records || [];
  return <div className="border-t pt-2 text-xs space-y-2">
    {value?.message && <p>{value.message}</p>}
    {bookings.length > 0 && <div className="flex flex-wrap gap-2">{bookings.map(booking => <a key={booking.id} className="inline-flex rounded-md border px-3 py-2 text-primary"
      href={tenantPath(`${bookingList}?booking_id=${encodeURIComponent(booking.id)}&detail=1`)}>Open booking {booking.booking_id || booking.id}</a>)}</div>}
    {Object.entries(value || {}).filter(([key, item]) => /(?:count|imported|skipped|valid|invalid|total|status)$/.test(key) && ["string", "number"].includes(typeof item))
      .map(([key, item]) => <p key={key}>{key.replaceAll("_", " ")}: {item}</p>)}
    {Array.isArray(rows) && rows.length > 0 && <div className="max-h-64 overflow-auto"><table className="w-full text-left"><thead><tr><th>Record</th><th>Status / review</th></tr></thead>
      <tbody>{rows.map((row, index) => <tr key={row.id || row.row_number || index} className="border-t"><td className="py-2 pr-2">{row.plot_no || row.booking?.booking_id || row.row_number || row.row_no || index + 1}</td>
        <td>{row.status || (row.valid === true ? "Valid" : row.valid === false ? "Invalid" : "Reviewed")}{row.message ? ` · ${row.message}` : ""}{Array.isArray(row.errors) && row.errors.length ? ` · ${row.errors.map(item => typeof item === "string" ? item : item.message || item.msg || "Review required").join("; ")}` : ""}</td></tr>)}</tbody></table></div>}
  </div>;
}
